import { ArrowLeft, Crosshair, MapPin } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../../lib/api.ts';
import { createCourse, currentPosition, getCourse, updateCourse } from '../../lib/courses.ts';

/* Ajouter ou corriger un golf.
 *
 * ── Ce que cet écran fait, et ce qu'il ne fait pas encore ───────────────────
 *
 * Il pose l'identité du parcours : son nom, sa ville, sa position et son
 * nombre de trous. Le CARNET — pars, index de difficulté, distances par boîte
 * de départ — attend un arbitrage sur la façon de le saisir, et les tables
 * l'accueillent déjà.
 *
 * ── La position ─────────────────────────────────────────────────────────────
 *
 * Le moyen le plus simple de situer un golf est d'y être : un bouton relève la
 * position de l'appareil. C'est le cas normal — on ajoute un golf en arrivant
 * dessus, pas depuis son canapé. La saisie manuelle des coordonnées reste
 * possible pour le cas contraire.
 *
 * Aucune carte : elle demanderait un fond de plan externe, donc une origine de
 * plus dans la politique de sécurité et une dépendance réseau sur un parking.
 * Deux nombres suffisent à trier par distance, qui est tout ce qu'on en fait.
 */

/* Les deux formats qu'on JOUE.
 *
 *  27 et 36 figuraient dans cette liste, par confusion entre le club et le
 *  parcours. Les clubs de cette taille existent — trois ou quatre boucles de
 *  neuf — mais on n'y joue jamais 27 trous d'affilée : ce sont trois parcours
 *  de 18 formés par les combinaisons de boucles, chacun avec sa carte. Un tel
 *  club se saisit donc en plusieurs parcours nommés. */
/* Les formats courants, du compact au parcours complet.
 *
 * Six et douze ne sont pas des curiosites : ce sont les formats des terrains
 * compacts et des golfs d'initiation, et le petit golf de Mornex en fait six.
 * Au-dela de dix-huit il n'y a rien a proposer — un club a 27 trous, ce sont
 * trois parcours de 18. */
const HOLE_CHOICES = [6, 9, 12, 18];

export function CourseEditor() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const editing = id !== undefined && id !== 'nouveau';

    const [name, setName] = useState('');
    const [city, setCity] = useState('');
    const [holeCount, setHoleCount] = useState(18);
    const [lat, setLat] = useState<string>('');
    const [lon, setLon] = useState<string>('');
    const [busy, setBusy] = useState(false);
    const [locating, setLocating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!editing) return;
        // Le parcours est demande PAR SON IDENTIFIANT.
        //
        // Cet ecran cherchait auparavant dans la premiere page de la liste, ce
        // qui marchait tant que le catalogue tenait en dix golfs et echouait
        // sur le onzieme -- une panne qui n'apparait qu'une fois le service
        // utilise, donc la pire espece.
        void getCourse(id)
            .then((c) => {
                setName(c.name);
                setCity(c.city ?? '');
                setHoleCount(c.holeCount);
                setLat(c.latitude == null ? '' : String(c.latitude));
                setLon(c.longitude == null ? '' : String(c.longitude));
            })
            .catch(() => setError('Chargement impossible'));
    }, [id, editing]);

    const releverPosition = async (): Promise<void> => {
        setLocating(true);
        const p = await currentPosition();
        setLocating(false);
        if (!p) {
            setError("Position indisponible. Autorisez la localisation, ou saisissez les coordonnées à la main.");
            return;
        }
        // Six décimales : environ onze centimètres, très au-delà de ce qu'un
        // club-house réclame, mais c'est ce que rend l'appareil et le tronquer
        // n'apporterait rien.
        setLat(p.lat.toFixed(6));
        setLon(p.lon.toFixed(6));
    };

    const enregistrer = async (): Promise<void> => {
        if (!name.trim()) { setError('Donnez un nom à ce golf'); return; }
        setBusy(true); setError(null);
        // Les deux coordonnées vont ensemble ou pas du tout : une latitude
        // seule ne situe rien, et la base refuse la paire incomplète.
        const la = lat.trim() === '' ? null : Number(lat);
        const lo = lon.trim() === '' ? null : Number(lon);
        const situe = la != null && lo != null && Number.isFinite(la) && Number.isFinite(lo);
        const body = {
            name: name.trim(),
            city: city.trim() || null,
            latitude: situe ? la : null,
            longitude: situe ? lo : null,
            holeCount,
        };
        try {
            if (editing) await updateCourse(id, body);
            else await createCourse(body);
            void navigate('/parcours');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Enregistrement impossible');
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4 py-2">
            <header>
                <Link to="/parcours" className="btn btn-ghost btn-sm mb-2 gap-2">
                    <ArrowLeft size={16} /> Parcours
                </Link>
                <h1 className="text-2xl font-bold tracking-tight">
                    {editing ? 'Modifier le golf' : 'Ajouter un golf'}
                </h1>
                {!editing && (
                    <p className="mt-1 text-sm opacity-70">
                        Il sera visible par tous les joueurs.
                    </p>
                )}
            </header>

            {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}

            <section className="space-y-3 rounded-box bg-base-200 p-4">
                <label className="block">
                    <span className="mb-1 block text-sm font-medium">Nom</span>
                    <input type="text" maxLength={120} className="input input-bordered h-14 w-full text-base"
                        placeholder="Golf de Saint-Cloud"
                        value={name} onChange={(e) => setName(e.target.value)} />
                </label>
                <label className="block">
                    <span className="mb-1 block text-sm font-medium">Ville</span>
                    <input type="text" maxLength={120} className="input input-bordered h-14 w-full text-base"
                        placeholder="Saint-Cloud"
                        value={city} onChange={(e) => setCity(e.target.value)} />
                    {/* La ville n'est pas décorative : elle départage deux golfs
                        de même nom, et c'est la seule indication de lieu quand
                        la position est refusée. */}
                    <span className="mt-1 block text-xs opacity-70">
                        Sert à vous repérer quand la localisation est refusée.
                    </span>
                </label>
            </section>

            <section className="rounded-box bg-base-200 p-4">
                <h2 className="text-sm font-medium">Nombre de trous</h2>
                <div className="mt-2 grid grid-cols-2 gap-2">
                    {HOLE_CHOICES.map((n) => (
                        <button key={n} type="button" onClick={() => setHoleCount(n)}
                            aria-pressed={holeCount === n}
                            className={`h-14 rounded-field text-base font-bold transition-colors ${
                                holeCount === n ? 'bg-primary text-primary-content' : 'bg-base-100 hover:bg-base-300'
                            }`}>
                            {n}
                        </button>
                    ))}
                </div>
            </section>

            <section className="rounded-box bg-base-200 p-4">
                <h2 className="text-sm font-medium">Emplacement</h2>
                <p className="mt-1 text-xs opacity-70">
                    Il sert à vous proposer les golfs les plus proches. Le plus simple :
                    relevez-le en étant sur place.
                </p>
                <button type="button" disabled={locating} onClick={() => void releverPosition()}
                    className="btn btn-outline btn-block mt-3 h-14 gap-2 tap-target">
                    <Crosshair size={18} />
                    {locating ? 'Relevé en cours…' : 'Utiliser ma position actuelle'}
                </button>

                <div className="mt-3 grid grid-cols-2 gap-2">
                    <label className="block">
                        <span className="mb-1 block text-xs opacity-70">Latitude</span>
                        <input type="text" inputMode="decimal" className="input input-bordered w-full tabular-nums"
                            placeholder="48.840000"
                            value={lat} onChange={(e) => setLat(e.target.value)} />
                    </label>
                    <label className="block">
                        <span className="mb-1 block text-xs opacity-70">Longitude</span>
                        <input type="text" inputMode="decimal" className="input input-bordered w-full tabular-nums"
                            placeholder="2.210000"
                            value={lon} onChange={(e) => setLon(e.target.value)} />
                    </label>
                </div>
                {lat && lon && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs opacity-70">
                        <MapPin size={13} className="shrink-0" aria-hidden="true" /> Position enregistrée.
                    </p>
                )}
            </section>

            <button type="button" disabled={busy} onClick={() => void enregistrer()}
                className="btn btn-primary btn-block h-14 text-lg font-bold tap-target">
                {editing ? 'Enregistrer' : 'Ajouter ce golf'}
            </button>
        </div>
    );
}
